import { useEffect, useRef, useCallback } from 'react';
import type { ScheduledProgram } from './useSchedules';

const STORAGE_KEY = 'ott_player_schedules';
const LEAD_MS = 5 * 60 * 1000;         // notify up to 5 min before start
const GRACE_AFTER_MS = 10 * 60 * 1000; // still notify up to 10 min after start
const CHECK_INTERVAL_MS = 30_000; // check every 30s

/**
 * Polls localStorage every 30 s and calls `onTrigger` once per scheduled program
 * when it's within 5 minutes of starting (and hasn't ended yet).
 * Each program is only triggered once per app session (tracked via a ref Set).
 */
export function useScheduleNotifier(onTrigger: (schedule: ScheduledProgram) => void) {
  const notifiedRef = useRef<Set<string>>(new Set());
  const onTriggerRef = useRef(onTrigger);
  onTriggerRef.current = onTrigger;

  const check = useCallback(() => {
    const nowMs = Date.now();
    let schedules: ScheduledProgram[] = [];
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      schedules = raw ? (JSON.parse(raw) as ScheduledProgram[]) : [];
    } catch {
      return;
    }

    // Sort ascending so we notify the soonest-starting program first
    const sorted = [...schedules].sort((a, b) => a.startMs - b.startMs);

    for (const s of sorted) {
      const key = `${s.streamId}:${s.startMs}`;
      if (notifiedRef.current.has(key)) continue;
      // Notify window: from LEAD_MS before start up to GRACE_AFTER_MS after start
      const timeToStart = s.startMs - nowMs;   // negative when already started
      const withinLead = timeToStart <= LEAD_MS;
      const notTooLate = nowMs - s.startMs <= GRACE_AFTER_MS;
      if (nowMs < s.endMs && withinLead && notTooLate) {
        notifiedRef.current.add(key);
        onTriggerRef.current(s);
        break; // show one modal at a time
      }
    }
  }, []);

  useEffect(() => {
    check(); // immediate check on mount
    const id = setInterval(check, CHECK_INTERVAL_MS);
    return () => clearInterval(id);
  }, [check]);
}
